import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, BookOpen, PlayCircle, FlaskConical } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { useCourse, useProgress, currentTierData } from "@/lib/hooks";
import { LessonVisual } from "@/components/LessonVisual";
import { Loader } from "@/components/ProtectedRoute";
import { PageShell } from "@/components/AppShell";
import { MachineTranslationBanner } from "@/components/LanguagePicker";
import { Md, PauseReveal, LessonIntro, LensBox, ProjectBox, VideoComing } from "@/components/LessonBlocks";
import { t, colon } from "@/lib/i18n";

const PartVideo = ({ v, slug }) => {
  const ref = useRef(null);
  const retried = useRef(false);
  const src = `${BACKEND_URL}/api/videos/${v.id}/stream`;
  const onError = async () => {
    if (retried.current || !ref.current) return;
    retried.current = true;
    const t = ref.current.currentTime;
    try { await api.post("/auth/refresh"); } catch { return; }
    ref.current.src = `${src}?r=${Date.now()}`;
    ref.current.currentTime = t;
    ref.current.play().catch(() => {});
  };
  return <video ref={ref} key={v.id} data-testid={`lesson-video-${slug}`} controls playsInline preload="metadata" src={src} onError={onError} onPlaying={() => { retried.current = false; }} className="aspect-video w-full rounded-xl bg-black" />;
};

const Blocks = ({ s, pausesOnly = false }) => (s.blocks || []).map((b, i) => (b.type === "pause"
  ? <PauseReveal key={`${s.slug}-${i}`} b={b} id={`${s.slug}-${i}`} />
  : !pausesOnly && <div key={`${s.slug}-${i}`} className="reader simple-reader"><Md>{b.body}</Md></div>));

const Part = ({ s, video, watch, admin, onText }) => (
  <section id={s.slug} data-testid={`lesson-section-${s.slug}`} className="fade-up">
    <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{s.title}</h2>
    {watch ? (
      <div className="mt-6">
        {video ? <PartVideo v={video} slug={s.slug} /> : <VideoComing s={s} admin={admin} onText={onText} />}
        <Blocks s={s} pausesOnly />
      </div>
    ) : <div className="mt-6"><Blocks s={s} /></div>}
  </section>
);

const ModeToggle = ({ watch, setWatch }) => (
  <div data-testid="lesson-mode-toggle" className="mt-6 grid grid-cols-2 gap-3 sm:max-w-md" role="group" aria-label={t("Text or video")}>
    {[[false, t("Text mode"), BookOpen, "read"], [true, t("Video mode"), PlayCircle, "watch"]].map(([w, label, Icon, id]) => (
      <button key={id} type="button" data-testid={`lesson-mode-${id}`} aria-pressed={watch === w} onClick={() => setWatch(w)}
        className={`flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3.5 text-base font-bold transition-[background-color,border-color,color,transform] ${watch === w ? "border-brand-primary bg-brand-primary text-brand-primary-fg shadow-md" : "border-brand-primary/40 bg-card text-brand-primary hover:-translate-y-0.5 hover:border-brand-primary"}`}>
        <Icon className="h-5 w-5" />{label}
      </button>
    ))}
  </div>
);

const PartTabs = ({ pages, page, setPage }) => (
  <div className="mt-6 flex gap-1.5" role="tablist">
    {pages.map((p, i) => (
      <button key={p.key} type="button" role="tab" aria-selected={i === page} title={p.label} data-testid={`lesson-part-tab-${i}`} onClick={() => setPage(i)}
        className={`h-2 flex-1 rounded-full transition-colors ${i < page ? "bg-brand-primary" : i === page ? "bg-brand-primary/60" : "bg-muted"}`} />
    ))}
  </div>
);

export default function LessonReader() {
  const { lessonId } = useParams();
  const { slug } = useBrand();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [watch, setWatchState] = useState(() => localStorage.getItem("cias_lesson_mode") === "watch");
  const setWatch = (w) => { setWatchState(w); localStorage.setItem("cias_lesson_mode", w ? "watch" : "read"); };
  const { user } = useAuth();
  const { data: course } = useCourse();
  const { data: progress } = useProgress();
  const { data: lesson, isError } = useQuery({ queryKey: ["lesson", lessonId, slug], queryFn: async () => (await api.get(`/course/lessons/${lessonId}`, { params: { edition: slug } })).data, enabled: !!slug, staleTime: 0, retry: false });

  useEffect(() => { setPage(0); }, [lessonId]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  if (isError) return <PageShell><p data-testid="lesson-not-found" className="text-lg">This lesson could not be found. <Link to="/learn" className="font-semibold text-brand-primary underline">Back to Learn</Link></p></PageShell>;
  if (!lesson || !course || !progress) return <Loader />;
  const isLab = lesson.kind === "lab";
  const done = currentTierData(progress).lessons_done.includes(lessonId);
  const parts = lesson.sections;
  const pages = [...parts.map((s) => ({ key: s.slug, label: s.title })), { key: "review", label: t("Review and finish") }];
  const last = page === pages.length - 1;

  const complete = async () => {
    if (lesson.kind === "lab") {
      navigate(lesson.next_id ? `/lessons/${lesson.next_id}` : `/modules/${lesson.module_id}`);
      return;
    }
    try {
      await api.post(`/progress/lessons/${lessonId}/complete`);
      await qc.invalidateQueries({ queryKey: ["progress"] });
      toast.success("Lesson complete");
      navigate(lesson.next_id ? `/lessons/${lesson.next_id}` : `/modules/${lesson.module_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const check = async () => {
    try {
      const { data } = await api.post("/quiz/start", { module_id: lesson.module_id, mode: "practice", lesson_id: lessonId });
      navigate(`/quiz/${data.attempt_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 pb-44 pt-8 sm:px-8 lg:pb-32">
      <nav data-testid="lesson-breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
        <Link to={`/modules/${lesson.module_id}`} className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-4 w-4" />{lesson.module_title}</Link>
        <span>·</span><span>{t("Lesson")} {lesson.index} {t("of")} {lesson.count}</span><span>·</span><span>~{lesson.minutes} min</span>
        {done && <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-brand-accent/15 px-2 py-0.5 text-xs font-semibold text-brand-accent"><Check className="h-3 w-3" />{t("Done")}</span>}
      </nav>
      <h1 data-testid="lesson-title" className="mt-4 font-heading text-3xl font-black tracking-tight sm:text-4xl">{lesson.title}</h1>
      <div className="mt-4"><MachineTranslationBanner /></div>
      <PartTabs pages={pages} page={page} setPage={setPage} />
      <div data-testid="lesson-part-label" className="mt-2 text-sm text-muted-foreground">{t("Step")} {page + 1} {t("of")} {pages.length}{colon()} {pages[page].label}</div>
      {page === 0 && <LessonIntro lesson={lesson} />}
      {!last && <ModeToggle watch={watch} setWatch={setWatch} />}
      <div className="mt-10">
        {!last ? <Part s={parts[page]} video={lesson.videos?.[parts[page].slug]} watch={watch} admin={user?.role === "admin"} onText={() => setWatch(false)} /> : (
          <div className="fade-up space-y-8">
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{t("Review and finish")}</h2>
            <LensBox lens={lesson.lens} />
            <ProjectBox project={lesson.project} next={lesson.next} />
            {lesson.visual && <div><div className="overline-label mb-3">{t("Try it")}</div><LessonVisual visual={lesson.visual} /></div>}
            {isLab && (
              <div data-testid="lesson-lab-card" className="rounded-lg border-2 border-brand-data bg-brand-data/5 p-6">
                <div className="font-heading text-lg font-bold">{t("Now do the interactive lab")}</div>
                <p className="mt-1 text-muted-foreground">{t("lab-card") === "lab-card" ? "This lab is required. Finishing it completes this lesson." : t("lab-card")}</p>
                <Link to={`/labs/${lesson.scenario_id}`} data-testid="lesson-start-lab-button" className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-data px-5 py-2.5 font-semibold text-white transition-opacity hover:opacity-90"><FlaskConical className="h-4 w-4" />{t("Start the lab")}</Link>
              </div>
            )}
            <div data-testid="lesson-check-card" className="rounded-lg border border-border bg-card p-6">
              <div className="font-heading text-lg font-bold">{t("Practice questions")}</div>
              <p className="mt-1 text-muted-foreground">{t("practice-card") === "practice-card" ? "5 questions from this lesson. No score is kept. Tap \"Show answer\" to check any question, or see all answers at the end." : t("practice-card")}</p>
              <button data-testid="lesson-practice-button" onClick={check} className="mt-4 rounded-full border-2 border-brand-primary px-5 py-2.5 font-semibold text-brand-primary transition-colors hover:bg-brand-primary/10">{t("Try 5 questions")}</button>
            </div>
          </div>
        )}
      </div>
      <div data-testid="lesson-bottom-bar" className="with-sidebar-left no-print fixed bottom-16 left-0 z-30 w-full border-t border-border bg-background/95 backdrop-blur-md lg:bottom-0">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-3 sm:px-8">
          {page > 0 ? <button data-testid="lesson-prev-button" onClick={() => setPage(page - 1)} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-semibold text-muted-foreground transition-colors hover:text-foreground"><ArrowLeft className="h-4 w-4" />{t("Back")}</button>
            : lesson.prev_id ? <Link data-testid="lesson-prev-link" to={`/lessons/${lesson.prev_id}`} className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />{t("Previous lesson")}</Link> : <span />}
          {last ? (isLab && !done ? <Link data-testid="lesson-complete-button" to={`/labs/${lesson.scenario_id}`} className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3 font-semibold text-brand-primary-fg transition-opacity hover:opacity-90">{t("Start the lab")}<ArrowRight className="h-4 w-4" /></Link>
            : <button data-testid="lesson-complete-button" onClick={complete} className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3 font-semibold text-brand-primary-fg transition-opacity hover:opacity-90">{done ? t("Next lesson") : t("Finish lesson")}<ArrowRight className="h-4 w-4" /></button>)
            : <button data-testid="lesson-next-step-button" onClick={() => setPage(page + 1)} className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3 font-semibold text-brand-primary-fg transition-opacity hover:opacity-90">{t("Next")}<ArrowRight className="h-4 w-4" /></button>}
        </div>
      </div>
    </div>
  );
}
